// src/model/search.js
// Search logic for the app, using contentService and GraphQL
import { fetchContent } from '../services/contentService';
import { getTreeForSubtopic } from './ContentTiles'; // Import tree utility for hydration

const _searchCache = {};
const _pageSize = 10;
let _currentPage = 0;
let _currentCursor = "";
let _hasMoreItems = false;
let _searchTotalCount = 0;
let _currentSearchString = "";
let _currentSearchLanguage = "";


function buildCacheKey(searchString, language, page) {
  return `${searchString.trim().toLowerCase()}-${language}-page${page}`;
}

/**
 * Returns to last search and hydrates results for display.
 * No arguments needed; uses last search state.
 * @returns {Promise<Array>} - Array of search result objects.
 */
export async function onReturnToSearch() {
  if (!_currentSearchString || !_currentSearchLanguage) return [];
  return await getSearchResults(_currentSearchString, _currentSearchLanguage, _currentPage);
}

/**
 * Change to a specific page of the last search.
 * @param {number} targetPage - The page number to load (0-based).
 * @returns {Promise<Array>} - Array of search result objects for the page.
 */
export async function onChangePage(targetPage) {
  if (!_currentSearchString || !_currentSearchLanguage) return [];
  _currentPage = targetPage;
  const results = await getSearchResults(_currentSearchString, _currentSearchLanguage, _currentPage);
  // Prefetch next page if it exists
  const totalPages = getTotalPages();
  if (_currentPage + 1 < totalPages) {
    getSearchResults(_currentSearchString, _currentSearchLanguage, _currentPage + 1);
  }
  return results;
}

/**
 * Performs a search using the ContentService and hydrates results for display.
 * Resets pagination state and starts at page 0.
 * @param {string} searchString - The search query string.
 * @param {string} language - The user's preferred language. Defaults to 'en'
 * @returns {Promise<Array>} - Array of search result objects.
 */
export async function onSearch(searchString, language = 'en') {
  _currentPage = 0; // reset page for new search
  _hasMoreItems = false;
  _currentCursor = ""; // reset cursor for new search
  _searchTotalCount = 0; // reset total count for new search
  _currentSearchString = searchString;
  _currentSearchLanguage = language;
  const results = await getSearchResults(searchString, language, _currentPage);
  // Prefetch next page if it exists
  const totalPages = getTotalPages();
  if (_currentPage + 1 < totalPages) {
    getSearchResults(searchString, language, _currentPage + 1);
  }
  return results;
}

/**
 * Returns the total number of pages for the last search.
 * @returns {number}
 */
export function getTotalPages() {
  return Math.max(1, Math.ceil(_searchTotalCount / _pageSize));
}

export function getCurrentPage() {
  return _currentPage;
}

async function getSearchResults(searchString, language, page) {
  const cacheKey = buildCacheKey(searchString, language, page);
  if (_searchCache[cacheKey]) {
    // Always update meta from cache
    const cached = _searchCache[cacheKey];
    _hasMoreItems = cached.meta.hasMoreItems;
    _currentCursor = cached.meta.cursor || "";
    _searchTotalCount = cached.meta.totalCount || 0;
    _currentSearchString = searchString;
    _currentSearchLanguage = language;
    return cached.results;
  }
  // For cursor-based pagination, we need to walk from page 0 to the target page to get the right cursor
  let cursor = "";
  if (page > 0) {
    for (let p = 0; p < page; p++) {
      const prevKey = buildCacheKey(searchString, language, p);
      if (!_searchCache[prevKey]) {
        await performSearchService(searchString, language, cursor);
      }
      cursor = _searchCache[prevKey]?.meta?.cursor || "";
    }
  }
  // Now fetch the target page
  const { results, meta } = await performSearchService(searchString, language, cursor);
  _searchCache[cacheKey] = { results, meta };
  // Always update meta after fetch
  _hasMoreItems = meta.hasMoreItems;
  _currentCursor = meta.cursor || "";
  _searchTotalCount = meta.totalCount || 0;
  _currentSearchString = searchString;
  _currentSearchLanguage = language;
  return results;
}

async function performSearchService(searchString, language, cursorOverride) {
  const gqlQuery = `
    query ($searchString: String!, $cursor: String!, $pageSize: Int!){
      listSubtopics(where: { articleContentText_contains: $searchString }, after: $cursor, limit: $pageSize) {
        data {
          id
          title
        }
        meta {
          totalCount
          hasMoreItems
          cursor
        }        
      }
    }`;
  const variables = {
    'searchString': searchString,
    'cursor': cursorOverride !== undefined ? cursorOverride : _currentCursor, // pagination
    'pageSize': _pageSize,
    'language': language
  };

  try {
    const data = await fetchContent(gqlQuery, variables);
    const results = (data && data.listSubtopics && Array.isArray(data.listSubtopics.data)) ? data.listSubtopics.data : [];
    const meta = {
      totalCount: data.listSubtopics.meta.totalCount || 0,
      hasMoreItems: data.listSubtopics.meta.hasMoreItems,
      cursor: data.listSubtopics.meta.cursor || ""
    };
    const hydrated = addTreeDetails(results);
    return { results: hydrated, meta };
  } catch (err) {
    // Log and fail fast
    console.error('Search error:', err);
    throw err;
  }
}

function addTreeDetails(subtopics) {
  // for each search result in listSubtopics, hydrate with additional info
  for (let i = 0; i < subtopics.length; i++) {
    const subtopic = subtopics[i];
    var tree = getTreeForSubtopic(subtopic.id);
    subtopic.groupTitle = tree.group.title;
    subtopic.topicTitle = tree.group.topic.title;
  }
  return subtopics;
}
